import React from "react";
import {
  OBJECTIVE_TYPE,
  REVIEW_STATUS,
  objectiveKey,
} from "../../_config/quarterlyOkrConfig";
import { StatePill } from "../QokrPrimitives/QokrPrimitives";
import "./_qokr-coach-remarks.scss";

// ═══════════════════════════════════════════════════════════════════════════
// "Coach Remarks" (§9.4) — what the coach said once their review was
// submitted: the note to the CEO, the agenda (to discuss, plus accepted
// objectives with a comment, grouped company / person; clicking one focuses
// its row on the board) and the company / person comments. The booked live
// review shows in the page banner instead.
// ═══════════════════════════════════════════════════════════════════════════

// The coach's comments on one objective (§9.3 `scope_type: "objective"`).
const objectiveComments = (comments, mark) =>
  comments.filter(
    (comment) =>
      comment.scope_type === "objective" &&
      String(
        mark.type === OBJECTIVE_TYPE.COMPANY
          ? comment.company_objective_id
          : comment.individual_objective_id,
      ) === String(mark.objective_id),
  );

// The agenda: objectives to discuss, and accepted ones the coach still had
// something to say about — company first, then each person's, under their
// name rather than repeated before every objective.
const agendaGroups = (marks, comments) => {
  const items = marks
    .map((mark) => ({ mark, notes: objectiveComments(comments, mark) }))
    .filter(
      ({ mark, notes }) =>
        mark.status === REVIEW_STATUS.TO_DISCUSS || notes.length > 0,
    )
    .sort(
      (a, b) =>
        (b.mark.status === REVIEW_STATUS.TO_DISCUSS) -
        (a.mark.status === REVIEW_STATUS.TO_DISCUSS),
    );
  const groups = new Map();
  items.forEach((item) => {
    const company = item.mark.type === OBJECTIVE_TYPE.COMPANY;
    const key = company ? "company" : String(item.mark.member?.user_id);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title: company ? "Company OKRs" : item.mark.member?.name || "Team",
        items: [],
      });
    }
    groups.get(key).items.push(item);
  });
  return [...groups.values()].sort(
    (a, b) => (b.key === "company") - (a.key === "company"),
  );
};

const QokrCoachRemarks = ({ remarks, onFocus }) => {
  const marks = remarks?.marks || [];
  const comments = remarks?.comments || [];
  const toDiscuss = marks.filter((m) => m.status === REVIEW_STATUS.TO_DISCUSS);
  const groups = agendaGroups(marks, comments);
  const commentsOn = (scope) => comments.filter((c) => c.scope_type === scope);

  return (
    <div className="qokr-remarks">
      <div className="qokr-remarks__head">
        <h2 className="qokr-remarks__title">Coach Remarks</h2>
        {toDiscuss.length ? (
          <span
            className="qokr-pill qokr-pill--amber"
            title={`${toDiscuss.length} to discuss`}
          >
            {toDiscuss.length}
          </span>
        ) : null}
      </div>

      {remarks?.note_to_ceo ? (
        <section className="qokr-remarks__section">
          <h3 className="qokr-remarks__label">Note from your coach</h3>
          <p className="qokr-remarks__note">{remarks.note_to_ceo}</p>
        </section>
      ) : null}

      {groups.map((group) => (
        <section className="qokr-remarks__section" key={group.key}>
          <h3 className="qokr-remarks__label">{group.title}</h3>
          <ul className="qokr-remarks__marks">
            {group.items.map(({ mark, notes }) => (
              <li key={objectiveKey(mark.type, mark.objective_id)}>
                <button
                  type="button"
                  className="qokr-remarks__mark"
                  onClick={() =>
                    onFocus(objectiveKey(mark.type, mark.objective_id))
                  }
                >
                  <span className="qokr-remarks__mark-text">
                    {mark.objective}
                  </span>
                  <StatePill state={mark.status} />
                </button>
                {notes.map((comment) => (
                  <p
                    key={comment.coach_comment_id}
                    className="qokr-remarks__comment qokr-remarks__comment--on"
                  >
                    {comment.comment_text}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {!groups.length ? (
        <p className="qokr-remarks__empty">
          Your coach accepted everything, with nothing to discuss.
        </p>
      ) : null}

      {[
        ["company", "On the company OKRs"],
        ["member", "For people"],
      ].map(([scope, label]) =>
        commentsOn(scope).length ? (
          <section className="qokr-remarks__section" key={scope}>
            <h3 className="qokr-remarks__label">{label}</h3>
            {commentsOn(scope).map((comment) => (
              <p
                key={comment.coach_comment_id}
                className="qokr-remarks__comment"
              >
                {comment.comment_text}
              </p>
            ))}
          </section>
        ) : null,
      )}
    </div>
  );
};

export default QokrCoachRemarks;
